import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Form, Input, Select, Tag, Tooltip, message } from 'antd';
import { BulbOutlined, CheckCircleOutlined, FileExcelOutlined, FilterOutlined, InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import DataTable from '@/components/data-table';
import { ClearAllFiltersButton, ToggleFilterButton } from '@/components/data-table-actions';
import { HowToCard } from '@/components/how-to-card';
import { Sidebar } from '@/components/sidebar';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import ContactReviewDialog from '@/components/team-assignments/ContactReviewDialog';
import { ReviewTag } from '@/components/team-assignments/contactReviewParts';
import { exportTeamContacts, getTeamContacts } from '@/services/teamAssignments.service';

const SECTIONS = ['INVOICES', 'BOOKKEEPING', 'PAYROLL', 'PAYMENTS', 'REPORTS'];
const CLIENT_STATUSES = ['ACTIVE', 'FORMER', 'TEST', 'OWN'];
const HOW_TO_ICONS = [SearchOutlined, FilterOutlined, FileExcelOutlined, InfoCircleOutlined];
const NO_CONTACT = 'ANY';
const REVIEW_FILTERS = ['UNCONFIRMED', 'NEEDS_RECONFIRMATION', 'CONFIRMED', 'CLIENT_UNCONFIRMED', 'CLIENT_CONFIRMED', 'CLIENT_CHANGED'];

// `contact…` keys: the board on the same page has its own `person` in the address.
const FILTER_DEFAULTS = Object.freeze({
  search: undefined,
  section: undefined,
  without: undefined,
  review: undefined,
  contactStatus: undefined,
  contactPerson: undefined,
});

// The address hands back a comma-separated value as a list and digits as a number.
const str = (v) => (Array.isArray(v) ? v.join(',') : v === undefined || v === null ? '' : String(v));
const list = (v) => (Array.isArray(v) ? v : v ? String(v).split(',') : []);

const saveBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

function ContactLine({ contact }) {
  const details = [contact.name, contact.position, contact.phone, contact.note].filter(Boolean);
  // A long address has no spaces to wrap at: it must break inside its own column.
  const line = <div className="whitespace-normal text-[13px] leading-5 [overflow-wrap:anywhere]">{contact.email || contact.name}</div>;
  return details.length ? (
    <Tooltip
      placement="topLeft"
      title={details.map((d) => (
        <div key={d}>{d}</div>
      ))}
    >
      {line}
    </Tooltip>
  ) : (
    line
  );
}

/**
 * Client contacts of every company the caller sees, one row per company and a
 * column per section, like the sheet Bora arranged. The server pages, filters
 * and decides which companies are visible; the export carries the same scope.
 *
 * @param {Object} props
 * @param {Array} props.people - the team, for the person filter
 * @param {boolean} props.seesEveryone - may filter by a colleague and by client status
 * @param {Function} props.onOpenCompany - opens one company's card on the board
 */
export default function ClientContactsView({ people = [], seesEveryone = false, onOpenCompany }) {
  const { t } = useTranslation(['teamContacts', 'teamAssignments', 'tables', 'forms']);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const [form] = Form.useForm();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [reviewOf, setReviewOf] = useState(null);
  const queryClient = useQueryClient();

  const filters = useMemo(
    () => ({
      search: str(queryParams.search) || undefined,
      section: str(queryParams.section) || undefined,
      without: str(queryParams.without) || undefined,
      review: str(queryParams.review) || undefined,
      clientStatus: str(queryParams.contactStatus) || undefined,
      person: str(queryParams.contactPerson) || undefined,
      order: str(queryParams.order) || undefined,
    }),
    [queryParams]
  );
  const offset = str(queryParams.offset) || '1';
  const limit = str(queryParams.limit) || '50';

  const { data, status, error } = useQuery({
    queryKey: ['team-contacts', filters, offset, limit],
    queryFn: () => getTeamContacts({ ...filters, offset, limit }),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const exporting = useMutation({
    mutationFn: () => exportTeamContacts(filters),
    onSuccess: (blob) => saveBlob(blob, `Kontakti_klijenata_${new Date().toISOString().slice(0, 10)}.xlsx`),
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  useEffect(() => {
    form.setFieldsValue({
      search: str(queryParams.search),
      section: str(queryParams.section) || undefined,
      without: str(queryParams.without) || undefined,
      review: str(queryParams.review) || undefined,
      contactStatus: list(queryParams.contactStatus),
      contactPerson: str(queryParams.contactPerson) || undefined,
    });
  }, [queryParams, form]);

  const resetFilters = () => {
    setQueryParams({ ...FILTER_DEFAULTS, offset: 1 });
    form.resetFields();
  };

  const applyFilters = (values) => {
    setQueryParams({
      search: values.search?.trim() || undefined,
      section: values.section || undefined,
      without: values.without || undefined,
      review: values.review || undefined,
      contactStatus: values.contactStatus?.length ? values.contactStatus.join(',') : undefined,
      contactPerson: values.contactPerson || undefined,
      offset: 1,
    });
    setFiltersOpen(false);
  };

  const sectionOptions = SECTIONS.map((s) => ({ value: s, label: t(`sections.${s}`) }));

  const columns = [
    {
      title: t('columns.company'),
      dataIndex: 'name',
      key: 'name',
      width: '17%',
      sorter: true,
      showSorterTooltip: false,
      sortOrder: queryParams.order === 'desc' ? 'descend' : 'ascend',
      onCell: () => ({ className: 'align-top' }),
      render: (name, row) => (
        <div className="whitespace-normal">
          <Tooltip title={row.fullName !== name ? row.fullName : null} placement="topLeft">
            {onOpenCompany ? (
              <button type="button" className="text-left font-semibold hover:underline [overflow-wrap:anywhere]" onClick={() => onOpenCompany(row._id)}>
                {name}
              </button>
            ) : (
              <span className="font-semibold [overflow-wrap:anywhere]">{name}</span>
            )}
          </Tooltip>
          {row.mergedNames?.length > 0 && (
            <Tooltip
              placement="topLeft"
              title={row.mergedNames.map((n) => (
                <div key={n}>{n}</div>
              ))}
            >
              <Tag className="ml-1">{t('merged', { count: row.mergedNames.length })}</Tag>
            </Tooltip>
          )}
          {row.clientStatus !== 'ACTIVE' && (
            <Tag className="ml-1">{t(`clientStatuses.${row.clientStatus}`, { ns: 'teamAssignments' })}</Tag>
          )}
        </div>
      ),
    },
    ...SECTIONS.map((s) => ({
      title: t(`sections.${s}`),
      key: s,
      width: '13.5%',
      onCell: () => ({ className: 'align-top' }),
      render: (_, row) =>
        row.sections?.[s]?.length ? (
          row.sections[s].map((c) => <ContactLine key={`${c.email}|${c.name}`} contact={c} />)
        ) : (
          <span className="opacity-40">—</span>
        ),
    })),
    {
      title: t('review.column'),
      key: 'review',
      width: '15.5%',
      onCell: () => ({ className: 'align-top' }),
      render: (_, row) => (
        <div className="grid gap-1 whitespace-normal">
          <ReviewTag label={t('review.hlb')} status={row.review?.status} by={row.review?.confirmedBy} at={row.review?.confirmedAt} />
          <ReviewTag label={t('review.client')} status={row.review?.clientStatus} by={row.review?.clientConfirmedBy} at={row.review?.clientConfirmedAt} />
          {row.review?.clientChanged && <Tag color="blue" className="m-0 whitespace-normal">{t('review.clientChangedTag')}</Tag>}
          <Button
            size="small"
            shape="round"
            className="w-fit"
            icon={<CheckCircleOutlined />}
            onClick={() => setReviewOf(row._id)}
          >
            {row.canEdit ? t('review.open') : t('review.view')}
          </Button>
        </div>
      ),
    },
  ];

  const howToSteps = [
    ...t('howTo.steps', { returnObjects: true }),
    ...(onOpenCompany ? t('howTo.cardStep', { returnObjects: true }) : []),
  ];

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-3">
      <HowToCard
        title={t('howTo.title')}
        sections={[
          {
            title: t('howTo.section'),
            steps: howToSteps.map((step, i) => ({ ...step, icon: HOW_TO_ICONS[i] || BulbOutlined })),
          },
        ]}
      />

      <DataTable
        rowKey="_id"
        title={`${t('title')}${data ? ` · ${data.count}` : ''}`}
        data={data?.items || []}
        total={data?.count || 0}
        status={status === 'pending' ? 'pending' : error ? 'error' : 'success'}
        columns={columns}
        offset={offset}
        limit={limit}
        onChangePagination={() => {}}
        onTableChange={({ sorter }) => {
          if (!sorter || Array.isArray(sorter)) return;
          setQueryParams({ order: sorter.order === 'descend' ? 'desc' : undefined, offset: 1 });
        }}
        tableLayout="fixed"
        size="small"
        locale={error ? { emptyText: error?.response?.data?.message || error.message } : undefined}
        headerChildren={
          <>
            <Button
              className="btn-default h-8 text-black"
              shape="round"
              icon={<FileExcelOutlined />}
              loading={exporting.isPending}
              onClick={() => exporting.mutate()}
            >
              {t('export')}
            </Button>
            <ToggleFilterButton label={t('filters', { ns: 'tables' })} onClick={() => setFiltersOpen(true)} defaultParams={FILTER_DEFAULTS} />
            <ClearAllFiltersButton label={t('clearAllFilters', { ns: 'tables' })} onReset={resetFilters} defaultParams={FILTER_DEFAULTS} />
          </>
        }
      />

      <Sidebar title={t('filters', { ns: 'tables' })} open={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <Form form={form} layout="vertical" className="form p-4" onFinish={applyFilters}>
          <Form.Item name="search" label={t('filter.search')}>
            <Input allowClear placeholder={t('filter.searchPlaceholder')} />
          </Form.Item>
          <Form.Item name="section" label={t('filter.section')}>
            <Select allowClear options={sectionOptions} placeholder={t('filter.any')} />
          </Form.Item>
          <Form.Item name="without" label={t('filter.without')}>
            <Select
              allowClear
              options={[{ value: NO_CONTACT, label: t('filter.withoutAny') }, ...sectionOptions]}
              placeholder={t('filter.any')}
            />
          </Form.Item>
          <Form.Item name="review" label={t('review.filter')}>
            <Select
              allowClear
              placeholder={t('filter.any')}
              options={REVIEW_FILTERS.map((value) => ({ value, label: t(`review.filters.${value}`) }))}
            />
          </Form.Item>
          {seesEveryone && (
            <Form.Item name="contactStatus" label={t('filter.clientStatus')}>
              <Select
                mode="multiple"
                allowClear
                placeholder={t('clientStatuses.ACTIVE', { ns: 'teamAssignments' })}
                options={CLIENT_STATUSES.map((s) => ({ value: s, label: t(`clientStatuses.${s}`, { ns: 'teamAssignments' }) }))}
              />
            </Form.Item>
          )}
          {seesEveryone && (
            <Form.Item name="contactPerson" label={t('filter.person')}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('filter.any')}
                options={people.map((p) => ({ value: p._id, label: `${p.firstName} ${p.lastName}`.trim() }))}
              />
            </Form.Item>
          )}
          <div className="form-footer form-footer--sticky">
            <div className="flex gap-2">
              <Button type="default" className="btn-default btn-default--hover" onClick={resetFilters}>
                {t('reset', { ns: 'forms' })}
              </Button>
              <Button type="primary" htmlType="submit" className="btn-primary">
                {t('submit', { ns: 'forms' })}
              </Button>
            </div>
          </div>
        </Form>
      </Sidebar>
      <ContactReviewDialog
        companyId={reviewOf}
        onClose={() => setReviewOf(null)}
        onChanged={() => queryClient.invalidateQueries({ queryKey: ['team-contacts'] })}
      />
    </div>
  );
}
